"use client";

import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useQuery } from "convex/react";
import { Truck, Store, Clock, MapPin, Search, ShoppingBag, ChevronRight, X, Phone, CalendarDays } from "lucide-react";
import { api } from "@convex/_generated/api";
import {
  isSectionServedAt,
  type SectionServiceWindows,
} from "@convex/lib/serviceWindows";
import { isMenuItemWebOrderable } from "@convex/lib/menuServicePolicy";
import { isRegularOrderingCatalogItem } from "@convex/lib/regularOrderingCatalog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCartStore, formatPrice, type OrderType } from "@/stores/cart-store";
import { useUIStore } from "@/stores/ui-store";
import {
  getActiveCategoryFromSectionTops,
  getMenuScrollSpyReadingLine,
} from "@/lib/menu-scrollspy";
import { MenuGrid } from "@/components/order/menu-grid";
import { PopularMenuGrid } from "@/components/order/popular-menu-grid";
import { CategoryNav } from "@/components/order/category-nav";
import { CategorySidebar } from "@/components/order/category-sidebar";
import { DeliveryAddressSheet } from "@/components/order/delivery-address-sheet";
import {
  CartSheet,
  nextPickupChipLabel,
  useOrderingPickupAvailability,
} from "@/components/order/cart-sheet";

import { useOrderingState } from "@/hooks/use-ordering-state";
import {
  resolveOrderTypeSelection,
  shouldResetDeliveryOrderType,
} from "@/lib/ordering-state";
import { guestFacingMenuItemName } from "@/lib/order-config";
import { telHref } from "@/lib/phone";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import type { Doc } from "../../../convex/_generated/dataModel";

type MenuItem = Doc<"menu_items">;
const MENU_LOAD_TIMEOUT_MS = 7000;

// The customization sheet pulls framer-motion and a Convex modifiers query.
// Lazy-loading it (ssr:false) keeps that ~heavy chunk out of the initial menu
// payload — it only downloads once a guest actually taps an item. One shared
// instance lives at the surface level (below), so the 60-100 menu cards no
// longer each mount their own copy.
const ItemCustomizationSheet = dynamic(
  () =>
    import("@/components/order/item-customization-sheet").then(
      (mod) => mod.ItemCustomizationSheet,
    ),
  { ssr: false },
);

// Flat category structures tuned by fulfillment mode.
// Pickup can expose the broader takeout menu; delivery stays travel-optimized.
export const PICKUP_CATEGORIES = [
  { id: "pizza", label: "Pizza", icon: "🍕" },
  { id: "handhelds", label: "Handhelds", icon: "🍔" },
  { id: "sandwiches", label: "Sandwiches", icon: "🥪" },
  { id: "pastas", label: "Pastas", icon: "🍝" },
  { id: "salads", label: "Salads", icon: "🥗" },
  { id: "starters", label: "Starters", icon: "🍤" },
  { id: "mains", label: "Mains", icon: "🍽️" },
  { id: "steaks", label: "Steaks", icon: "🥩" },
  { id: "seafood", label: "Seafood", icon: "🐟" },
  { id: "sides", label: "Sides", icon: "🍟" },
  { id: "desserts", label: "Desserts", icon: "🍰" },
  { id: "brunch_mains", label: "Brunch Mains", icon: "🍳" },
  { id: "brunch_benedicts", label: "Benedicts", icon: "🍳" },
  { id: "brunch_sweets", label: "Brunch Sweets", icon: "🥞" },
  { id: "brunch_salads", label: "Brunch Salads", icon: "🥗" },
  { id: "brunch_sides", label: "Brunch Sides", icon: "🍟" },
  { id: "beverages", label: "Beverages", icon: "🥤" },
  { id: "energy_drinks", label: "Energy Drinks", icon: "⚡" },
] as const;

export const DELIVERY_CATEGORIES = [
  { id: "pizza", label: "Pizza", icon: "🍕" },
  { id: "handhelds", label: "Handhelds", icon: "🍔" },
  { id: "sandwiches", label: "Sandwiches", icon: "🥪" },
  { id: "pastas", label: "Pastas", icon: "🍝" },
  { id: "salads", label: "Salads", icon: "🥗" },
  { id: "starters", label: "Starters", icon: "🍤" },
  { id: "mains", label: "Mains", icon: "🍽️" },
  { id: "steaks", label: "Steaks", icon: "🥩" },
  { id: "seafood", label: "Seafood", icon: "🐟" },
  { id: "sides", label: "Sides", icon: "🍟" },
  { id: "desserts", label: "Desserts", icon: "🍰" },
  { id: "beverages", label: "Beverages", icon: "🥤" },
  { id: "energy_drinks", label: "Energy Drinks", icon: "⚡" },
] as const;

type CategoryConfig = (typeof PICKUP_CATEGORIES)[number] | (typeof DELIVERY_CATEGORIES)[number];
type CategoryId = CategoryConfig["id"];

function compareMenuOrder<T extends { sortOrder?: number; name: string }>(a: T, b: T) {
  const ao = a.sortOrder ?? 999_999;
  const bo = b.sortOrder ?? 999_999;
  if (ao !== bo) return ao - bo;
  return a.name.localeCompare(b.name);
}

function normalizeDeepLinkName(value: string) {
  return value
    .replace(/^\*/, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

// Free-text `originalSection` values (as they appeared on the source menu PDF)
// to exclude from delivery.
const EXCLUDED_SECTIONS = new Set(["Brunch Menu", "Specials"]);

// Categories that do not travel, excluded from delivery only. This set used to
// be called LUNCH_CATEGORIES, which was misleading: no restaurant here serves
// lunch, and the reason these are hidden is that they arrive soggy, not that
// they belong to a meal period. The name mattered — six live `sandwiches` items
// are kept off the delivery menu by this set alone, so retiring the lunch
// section would have silently put them on it.
const DELIVERY_EXCLUDED_CATEGORIES = new Set([
  "soups",
  "combo",
  "sandwiches",
  "panini",
  "entrees",
]);

/**
 * Whether a menu item should appear while browsing.
 *
 * `serviceWindows` comes from the tenant's own order settings and `effectiveAt`
 * is the instant the food is handed over (a scheduled pickup time, else now),
 * so this matches the server rule in convex/orders.ts exactly — a guest never
 * builds a cart the server will refuse. Keying the section gate on
 * `item.section` and never on a category id is deliberate: one host-aware build
 * serves every restaurant, and a section literal here would paint one tenant's
 * schedule onto another tenant's menu. A section nobody configured stays
 * visible (see isSectionServedAt).
 */
export function isItemAvailableForBrowsing(
  item: {
    restaurantId?: string;
    locationId?: string;
    name?: string;
    isAlcohol?: boolean;
    originalSection?: string;
    section?: string;
    category?: string;
    servicePolicy?: { orderability: "not_orderable" };
  },
  orderType: OrderType,
  categories: readonly CategoryConfig[],
  serviceWindows: SectionServiceWindows | undefined,
  effectiveAt: number,
  catalogScope?: { restaurantId?: string; locationId?: string },
) {
  if (!isMenuItemWebOrderable(item)) return false;
  // Host/location scope must match orders:create. Item rows can omit
  // restaurantId/locationId; without this the Roma alcohol/catering gate
  // no-ops and the guest can add items the server rejects.
  if (!isRegularOrderingCatalogItem(item, catalogScope ?? item)) return false;
  if (item.section === "catering") return false;

  if (!isSectionServedAt(serviceWindows, item.section, effectiveAt)) return false;

  if (orderType === "delivery") {
    if (item.originalSection && EXCLUDED_SECTIONS.has(item.originalSection)) return false;
    if (item.category && DELIVERY_EXCLUDED_CATEGORIES.has(item.category)) return false;
  }

  return categories.some((category) => category.id === item.category);
}

export function OrderingMenuSurface({
  showMenuHeader = false,
  initialMenuItems,
  liveMenuQueryEnabled = true,
}: {
  showMenuHeader?: boolean;
  initialMenuItems?: MenuItem[];
  liveMenuQueryEnabled?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>("guest-favorites");
  // A single, surface-level customization target. Card taps set it via onSelect,
  // and the deep-link effect (below) seeds it from the ?item / ?itemName params
  // on load. Exactly one shared <ItemCustomizationSheet> is driven by this — no
  // longer one sheet per card.
  const [selectedItem, setSelectedItem] = useState<Doc<"menu_items"> | null>(null);
  const [deliveryAddressOpen, setDeliveryAddressOpen] = useState(false);
  const [menuLoadTimedOut, setMenuLoadTimedOut] = useState(false);
  const { searchQuery, setSearchQuery } = useUIStore();
  const categoryRefs = useRef<Map<string, HTMLElement>>(new Map());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const controlsBarRef = useRef<HTMLDivElement>(null);

  // The y-coordinate (from viewport top) where menu content starts being
  // occluded by sticky chrome = the bottom edge of the pinned controls bar.
  // Measuring its rect bottom directly is surface-agnostic: it already includes
  // whatever header sits above it (the h-14 order header on the order app, or
  // the marketing site header on the /menu embed) plus the bar's own height,
  // however it wraps on a given viewport. Falls back to a sane constant before
  // the ref attaches.
  const getStickyOffset = () => {
    const bar = controlsBarRef.current;
    if (bar) {
      return Math.round(bar.getBoundingClientRect().bottom);
    }
    return 112;
  };

  useLayoutEffect(() => {
    const bar = controlsBarRef.current;
    const shell = bar?.closest<HTMLElement>(".owner-style-menu-shell");
    const apply = () => {
      const offset = getStickyOffset();
      shell?.style.setProperty("--order-sticky-offset", `${offset}px`);
    };
    apply();
    if (!bar || !shell) return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(bar);
    window.addEventListener("resize", apply);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [showMenuHeader]);
  const orderType = useCartStore((state) => state.orderType);
  const scheduledFor = useCartStore((state) => state.scheduledFor);
  const deliveryAddress = useCartStore((state) => state.deliveryAddress);
  const deliveryZoneId = useCartStore((state) => state.deliveryZoneId);
  const setOrderType = useCartStore((state) => state.setOrderType);
  const { location, siteConfig: hostSiteConfig } = useMarketingLocationContext();
  const catalogScope = useMemo(
    () => ({
      restaurantId: location.restaurantId,
      locationId: location.id,
    }),
    [location.id, location.restaurantId],
  );
  // Brand identity (H1 + brand-named copy) MUST follow the request host, not the
  // build-time env siteConfig. A build-time constant bakes in whatever
  // NEXT_PUBLIC_SITE_URL resolves to (e.g. "Roma Kitchen" on the dark-funnel dev
  // server), which leaked onto the Tuscan host. Derive these from the
  // host-resolved siteConfig so /menu stays tenant-correct.
  const isTuscanHost = hostSiteConfig.name === "Tuscan Cove Bar & Patio";
  const brandShortName = isTuscanHost ? "Tuscan Cove" : hostSiteConfig.name;

  // Browse-only mode (NEXT_PUBLIC_DISABLE_ORDER) skips the availability query
  // entirely and renders no hours banner — nothing about online ordering
  // hours should appear when first-party ordering is not live.
  const {
    firstPartyOrderingEnabled,
    orderingPaused,
    isLoading: orderingStateLoading,
    // Static capability AND runtime operator switch (fails dark while loading).
    deliveryOrderingEnabled,
    fallbackPhone,
    // Per-section service hours from the tenant's order settings. Undefined for
    // any tenant that configured none, which keeps every section visible.
    serviceWindows,
  } = useOrderingState();
  // Minute-resolution clock, same pattern (and 30s interval) as
  // useOrderingPickupAvailability in cart-sheet.tsx: the browse memos below have
  // no other time dependency, so without this tick a section would stay on the
  // menu past its close for anyone holding the page open.
  const [nowMinute, setNowMinute] = useState(
    () => Math.floor(Date.now() / 60000) * 60000,
  );
  useEffect(() => {
    const id = setInterval(
      () => setNowMinute(Math.floor(Date.now() / 60000) * 60000),
      30 * 1000,
    );
    return () => clearInterval(id);
  }, []);
  // A scheduled order is judged at the pickup instant, not at browse time, so a
  // guest scheduling next Sunday 11:00 sees brunch. Delivery clears
  // scheduledFor in the cart store, so this falls back to now there.
  //
  // Only a FUTURE scheduledFor counts. It is persisted to localStorage
  // (cart-store `partialize`) and nothing clears it once the time it names has
  // passed, so a guest who scheduled an order last Tuesday and came back during
  // Sunday brunch had the whole menu judged against a dead timestamp — brunch
  // stayed hidden through real brunch service. A past instant carries no
  // intent; `now` does.
  const effectiveAt =
    scheduledFor !== null && scheduledFor > nowMinute ? scheduledFor : nowMinute;
  const { orderingClosed, nextOpenAt } = useOrderingPickupAvailability(
    firstPartyOrderingEnabled,
  );
  // Quick-add guard set, fetched once per surface and gated exactly like the
  // availability probe: browse-only mode (NEXT_PUBLIC_DISABLE_ORDER) skips the
  // query and keeps the + button opening the sheet as before. While the set is
  // loading the guard stays null, which also disables one-tap add.
  const requiredModifierItemIds = useQuery(
    api.menu.getMenuItemIdsWithRequiredModifiers,
    firstPartyOrderingEnabled ? { locationId: location.id } : "skip",
  );
  const quickAddRequiredIds = useMemo(
    () =>
      firstPartyOrderingEnabled && requiredModifierItemIds
        ? new Set<string>(requiredModifierItemIds)
        : null,
    [firstPartyOrderingEnabled, requiredModifierItemIds],
  );
  const browsingCategories: readonly CategoryConfig[] =
    orderType === "delivery" && deliveryOrderingEnabled
      ? DELIVERY_CATEGORIES
      : PICKUP_CATEGORIES;

  // Prevent hydration mismatch for Radix components
  useEffect(() => {
    setMounted(true);
  }, []);

  // Pickup-only mode: never leave persisted carts stuck on "delivery"
  useEffect(() => {
    if (
      shouldResetDeliveryOrderType({
        isLoading: orderingStateLoading,
        deliveryOrderingEnabled,
        orderType,
      })
    ) {
      setOrderType("pickup");
    }
  }, [
    deliveryOrderingEnabled,
    orderType,
    orderingStateLoading,
    setOrderType,
  ]);

  const handleOrderTypeChange = useCallback(
    (requestedOrderType: OrderType) => {
      const selection = resolveOrderTypeSelection({
        requestedOrderType,
        hasDeliveryAddress: Boolean(deliveryAddress),
        hasDeliveryZone: Boolean(deliveryZoneId),
      });

      if (selection === "prompt_address") {
        setDeliveryAddressOpen(true);
        return;
      }

      setOrderType(selection);
    },
    [deliveryAddress, deliveryZoneId, setOrderType],
  );

  // Fetch menu items scoped to the active public location. The public /menu
  // route can pass server-fetched rows and skip this live query so transient
  // client network failures do not turn the marketing menu into a dead spinner.
  const liveMenuItems = useQuery(
    api.menu.getAvailable,
    liveMenuQueryEnabled ? { locationId: location.id } : "skip",
  ) as MenuItem[] | undefined;
  const rawMenuItems = liveMenuItems ?? initialMenuItems;
  const menuItems = useMemo(() => rawMenuItems?.map((item) => {
    const guestName = guestFacingMenuItemName(item.name);
    const named = guestName === item.name ? item : { ...item, name: guestName };
    // Explicitly non-alcoholic beer/mocktails belong under Beverages in the
    // food-ordering UI, which intentionally has no alcohol category tabs.
    if (named.restaurantId === "roma-kitchen" && named.locationId === "roma-kitchen-southern-highlands" &&
      ["beer", "wine", "cocktails", "spirits"].includes(named.category) && isRegularOrderingCatalogItem(named, catalogScope)) {
      return { ...named, category: "beverages" as const, originalSection: "Non-Alcoholic Drinks" };
    }
    return named;
  }), [catalogScope, rawMenuItems]);

  // /menu may explain a row-scoped dine-in program, but /order must never
  // surface one as a purchasable menu card. Group only explicit policies from
  // the tenant-scoped rows returned for this host; no menu labels, categories,
  // or sections participate in the decision.
  const servicePolicyPrograms = useMemo(() => {
    if (!showMenuHeader || !menuItems) return [];

    const programs = new Map<
      string,
      {
        policy: NonNullable<MenuItem["servicePolicy"]>;
        items: MenuItem[];
      }
    >();

    for (const item of menuItems) {
      const policy = item.servicePolicy;
      if (!item.isAvailable || !policy) continue;

      const program = programs.get(policy.programId);
      if (program) {
        program.items.push(item);
      } else {
        programs.set(policy.programId, { policy, items: [item] });
      }
    }

    return [...programs.values()];
  }, [menuItems, showMenuHeader]);

  useEffect(() => {
    if (menuItems !== undefined || !liveMenuQueryEnabled) {
      setMenuLoadTimedOut(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setMenuLoadTimedOut(true);
    }, MENU_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [liveMenuQueryEnabled, location.id, menuItems]);

  // Search logic
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || !menuItems) return [];
    const query = searchQuery.toLowerCase().trim();
    return menuItems
      .filter((item) => {
        if (
          !isItemAvailableForBrowsing(
            item,
            orderType,
            browsingCategories,
            serviceWindows,
            effectiveAt,
            catalogScope,
          )
        ) {
          return false;
        }

        return (
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.category?.toLowerCase().includes(query)
        );
      })
      .sort(compareMenuOrder);
  }, [
    browsingCategories,
    effectiveAt,
    orderType,
    searchQuery,
    catalogScope,
    serviceWindows,
    menuItems,
  ]);

  // Handle keyboard shortcuts — Cmd/Ctrl-K focuses the inline search field,
  // Escape clears it (and blurs) so the single search entry point still
  // responds to the power-user shortcut after the header takeover was removed.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
      // Only clear search when the search field itself is focused, so pressing
      // Escape to dismiss the item/cart modal (a Radix overlay owns that Escape)
      // doesn't also wipe the guest's search results underneath.
      if (
        e.key === "Escape" &&
        document.activeElement === searchInputRef.current
      ) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setSearchQuery]);

  // Filter and group items by category (excluding brunch, specials, etc.)
  const itemsByCategory = useMemo(() => {
    if (!menuItems) return {} as Record<CategoryId, typeof menuItems>;

    const result: Record<string, typeof menuItems> = {};

    menuItems.forEach((item) => {
      const category = browsingCategories.find((c) => c.id === item.category);
      if (!category) return;
      if (
        !isItemAvailableForBrowsing(
          item,
          orderType,
          browsingCategories,
          serviceWindows,
          effectiveAt,
          catalogScope,
        )
      ) {
        return;
      }

      if (!result[item.category]) {
        result[item.category] = [];
      }
      result[item.category].push(item);
    });

    (Object.keys(result) as CategoryId[]).forEach((key) => {
      result[key].sort(compareMenuOrder);
    });

    return result as Record<CategoryId, typeof menuItems>;
  }, [browsingCategories, catalogScope, effectiveAt, orderType, serviceWindows, menuItems]);

  // Get "Most Popular" items, driven by the seed's isPopular flag rather than
  // price. Photographed favorites lead (the hero row sells on imagery); if too
  // few flagged items have photos, fall back to other flagged items, then to
  // the most expensive photographed items so the row is never sparse.
  const guestFavoriteItems = useMemo(() => {
    if (!menuItems) return [];

    const POPULAR_LIMIT = 6;

    const browsable = menuItems.filter((item) =>
      isItemAvailableForBrowsing(
        item,
        orderType,
        browsingCategories,
        serviceWindows,
        effectiveAt,
        catalogScope,
      ),
    );

    const flaggedWithPhoto = browsable
      .filter((item) => item.isPopular && item.photoUrl)
      .sort(compareMenuOrder);
    const flaggedNoPhoto = browsable
      .filter((item) => item.isPopular && !item.photoUrl)
      .sort(compareMenuOrder);
    const photographedByPrice = browsable
      .filter((item) => item.photoUrl)
      .sort((a, b) => b.price - a.price);

    const selected: typeof browsable = [];
    const seen = new Set<string>();
    for (const pool of [flaggedWithPhoto, flaggedNoPhoto, photographedByPrice]) {
      for (const item of pool) {
        if (selected.length >= POPULAR_LIMIT) break;
        if (seen.has(item._id)) continue;
        seen.add(item._id);
        selected.push(item);
      }
      if (selected.length >= POPULAR_LIMIT) break;
    }

    return selected;
  }, [browsingCategories, catalogScope, effectiveAt, orderType, serviceWindows, menuItems]);

  const guestFavoritePhotoUrls = useMemo(
    () =>
      new Set(
        guestFavoriteItems
          .map((item) => item.photoUrl)
          .filter((photoUrl): photoUrl is string => Boolean(photoUrl)),
      ),
    [guestFavoriteItems],
  );

  const itemsByDeepLinkId = useMemo(() => {
    if (!menuItems) return new Map<string, Doc<"menu_items">>();

    return new Map(
      menuItems
        .filter((item) =>
          isItemAvailableForBrowsing(
            item,
            orderType,
            browsingCategories,
            serviceWindows,
            effectiveAt,
            catalogScope,
          ),
        )
        .map((item) => [item._id, item]),
    );
  }, [browsingCategories, catalogScope, effectiveAt, menuItems, orderType, serviceWindows]);

  const registerCategoryRef = useCallback(
    (categoryKey: string) => (element: HTMLElement | null) => {
      if (element) {
        categoryRefs.current.set(categoryKey, element);
        return;
      }

      categoryRefs.current.delete(categoryKey);
    },
    [],
  );

  // Get available categories for navigation (only those with items)
  const availableCategories = useMemo(() => {
    const cats: Array<{ key: string; label: string; icon?: string }> = [];

    // Add guest favorites first if there are any
    if (guestFavoriteItems.length > 0) {
      cats.push({
        key: "guest-favorites",
        label: "Most Popular",
        icon: "🔥",
      });
    }

    browsingCategories.forEach((cat) => {
      if ((itemsByCategory[cat.id]?.length ?? 0) > 0) {
        cats.push({ key: cat.id, label: cat.label, icon: cat.icon });
      }
    });

    return cats;
  }, [browsingCategories, itemsByCategory, guestFavoriteItems]);

  // Scroll to category when clicked
  const scrollToCategory = useCallback((categoryKey: string) => {
    const scroll = () => {
      const element = categoryRefs.current.get(categoryKey);
      if (!element) return false;

      const headerOffset = getStickyOffset();
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      window.scrollTo({
        top: offsetPosition,
        behavior: prefersReduced ? "auto" : "smooth",
      });
      setActiveCategory(categoryKey);
      return true;
    };

    if (searchQuery.trim()) {
      setSearchQuery("");
      requestAnimationFrame(() => {
        requestAnimationFrame(scroll);
      });
      return;
    }

    scroll();
  }, [searchQuery, setSearchQuery]);

  const handleCategoryClick = useCallback((categoryKey: string) => {
    const element = categoryRefs.current.get(categoryKey);
    const hasSearch = searchQuery.trim().length > 0;

    if (!element && !hasSearch) {
      return;
    }

    scrollToCategory(categoryKey);
  }, [scrollToCategory, searchQuery]);

  // Seed the shared selection from a ?item / ?itemName deep link exactly once,
  // as soon as the menu is available. A ref guard keeps this from re-running and
  // clobbering a selection the guest made by tapping a card (the effect shares
  // the selectedItem state with card taps now).
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (!menuItems || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const itemId = params.get("item");
    const itemName = params.get("itemName");
    if (!itemId && !itemName) {
      // No deep link to apply; mark done so a later card tap isn't cleared.
      deepLinkAppliedRef.current = true;
      return;
    }

    deepLinkAppliedRef.current = true;

    const itemNameKey = itemName ? normalizeDeepLinkName(itemName) : "";
    const item =
      (itemId ? itemsByDeepLinkId.get(itemId) : undefined) ??
      (itemNameKey
        ? [...itemsByDeepLinkId.values()].find(
            (candidate) =>
              normalizeDeepLinkName(candidate.name) === itemNameKey,
          )
        : undefined) ??
      null;

    if (!item) return;

    setSelectedItem(item);

    if (item.category) {
      setActiveCategory(item.category);
      requestAnimationFrame(() => scrollToCategory(item.category));
    }
  }, [itemsByDeepLinkId, menuItems, scrollToCategory]);

  const closeSelectedItem = (open: boolean) => {
    if (open) return;

    setSelectedItem(null);
    if (typeof window === "undefined") return;

    // Scrub the deep-link params so re-opening the surface (or a shared link)
    // doesn't re-trigger the item. Harmless no-op when they were never present.
    const params = new URLSearchParams(window.location.search);
    params.delete("item");
    params.delete("itemName");
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`,
    );
  };

  // Update active category on scroll
  useEffect(() => {
    let ticking = false;
    let raf = 0;
    const measure = () => {
      ticking = false;
      const chromeBottom = getStickyOffset();
      const readingLine = Math.max(
        getMenuScrollSpyReadingLine({
          viewportHeight: window.innerHeight,
          rootScrollPaddingTop: 0,
          sectionScrollMarginTop: chromeBottom,
        }),
        chromeBottom + 80,
      );
      const sections = availableCategories.flatMap((cat) => {
        const element = categoryRefs.current.get(cat.key);
        if (!element) return [];
        const heading = element.querySelector("h2");
        return [
          {
            key: cat.key,
            top: (heading ?? element).getBoundingClientRect().top,
          },
        ];
      });
      const documentEl = document.documentElement;
      const pinnedToEnd =
        window.scrollY + window.innerHeight >= documentEl.scrollHeight - 24;
      const currentCategory = getActiveCategoryFromSectionTops(
        sections,
        readingLine,
        { pinnedToEnd },
      );

      if (currentCategory && currentCategory !== activeCategory) {
        setActiveCategory(currentCategory);
      }
    };

    // Throttle to one measurement per animation frame — getBoundingClientRect on
    // every raw scroll event (now also measuring the pinned controls bar) forces
    // layout repeatedly and can jank the menu scroll on lower-end phones.
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      raf = requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    measure();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(raf);
    };
  }, [activeCategory, availableCategories]);

  const isLoading = menuItems === undefined && !menuLoadTimedOut;
  const menuUnavailable = menuItems === undefined && menuLoadTimedOut;
  const menuEmpty = menuItems !== undefined && menuItems.length === 0;

  return (
    <div className="owner-style-menu-shell min-h-screen bg-white pb-[var(--sticky-bar-clearance)] text-[#0d0d0d] lg:pb-12">
      <div
        ref={controlsBarRef}
        className={
          showMenuHeader
            ? // /menu embed: pin beneath the fixed marketing site header.
              "sticky top-[var(--site-header-height)] z-30 border-b border-[#ededed] bg-white"
            : // Order app: pin beneath the h-14 order header (single top bar).
              "sticky top-14 z-30 -mt-px border-b border-[#ededed] bg-white pt-px"
        }
      >
        <div className="mx-auto flex max-w-7xl flex-col gap-1.5 px-4 py-1.5 sm:gap-3 sm:px-6 sm:py-3 lg:px-8">
          {showMenuHeader ? (
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col gap-0.5">
                <h1 className="text-[22px] font-bold leading-7 tracking-normal text-[#0d0d0d] sm:text-[28px] sm:leading-9 lg:text-[32px]">
                  {`${brandShortName} Menu`}
                </h1>
                <p className="hidden max-w-2xl text-[14px] font-medium leading-5 tracking-normal text-[#575757] sm:block sm:text-[15px] sm:leading-6">
                  {orderingPaused
                    ? `Online ordering is paused for now. Browse the menu, then call ${brandShortName} to place a pickup order.`
                    : "Browse the menu, build your pickup order, and we’ll have it ready when you get here."}
                </p>
              </div>
              {/* The /menu embed has no order-layout header, so the surface
                  keeps its own cart control (desktop especially). The /order
                  app omits this — the order layout header owns the cart. */}
              <CartButton mounted={mounted} />
            </div>
          ) : null}

          {/* Pickup-only: one compact row (time chip + search) on mobile.
              Delivery-enabled tenants keep a stacked mobile layout so the
              Pickup/Delivery tabs are not crushed beside search. */}
          <div
            className={
              deliveryOrderingEnabled
                ? "flex flex-col gap-1.5 lg:flex-row lg:items-center lg:justify-between lg:gap-2"
                : "flex flex-row items-center gap-1.5"
            }
          >
            <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:gap-2" aria-label="Order type">
              {mounted ? (
                deliveryOrderingEnabled ? (
                  <Tabs
                    value={orderType}
                    onValueChange={(v) =>
                      handleOrderTypeChange(v as OrderType)
                    }
                    className="w-auto shrink-0"
                  >
                    <TabsList className="h-9 rounded-full bg-[#ebebec] p-1 sm:h-10">
                      <TabsTrigger
                        value="pickup"
                        className="h-7 rounded-full px-3 text-[13px] font-semibold text-[#444] data-[state=active]:bg-white data-[state=active]:text-[#0d0d0d] data-[state=active]:shadow-[0_1px_3px_rgb(0_0_0_/_0.16)] sm:h-8 sm:px-4 sm:text-sm"
                      >
                        <Store className="mr-1 h-3.5 w-3.5 sm:mr-1.5 sm:h-4 sm:w-4" />
                        Pickup
                      </TabsTrigger>
                      <TabsTrigger
                        value="delivery"
                        className="h-7 rounded-full px-3 text-[13px] font-semibold text-[#444] data-[state=active]:bg-white data-[state=active]:text-[#0d0d0d] data-[state=active]:shadow-[0_1px_3px_rgb(0_0_0_/_0.16)] sm:h-8 sm:px-4 sm:text-sm"
                      >
                        <Truck className="mr-1 h-3.5 w-3.5 sm:mr-1.5 sm:h-4 sm:w-4" />
                        Delivery
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                ) : (
                  // Redundant on mobile: the timing chip already says
                  // "Opens …" when closed, and open hours show ASAP ETA.
                  <div className="hidden h-9 items-center gap-2 rounded-full bg-[#ebebec] p-1 text-[13px] font-semibold sm:inline-flex sm:h-10 sm:text-sm">
                    <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-white px-3 shadow-[0_1px_3px_rgb(0_0_0_/_0.16)] sm:h-8 sm:px-4">
                      <Store className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      Pickup
                    </span>
                  </div>
                )
              ) : (
                <div className="hidden h-9 w-40 animate-pulse rounded-full bg-[#ebebec] sm:block sm:h-10 sm:w-44" />
              )}

              <div className="inline-flex h-9 max-w-[min(100%,11.5rem)] items-center gap-1 rounded-[10px] border border-[#d6d6d6] bg-white px-2 text-[12px] font-medium text-[#0d0d0d] shadow-[0_1px_2px_rgb(0_0_0_/_0.05)] sm:max-w-none sm:h-10 sm:gap-2 sm:px-3 sm:text-sm">
                <Clock className="h-3.5 w-3.5 shrink-0 text-[#707070] sm:h-4 sm:w-4" />
                <span className="truncate">
                  {orderingClosed ? (
                    nextOpenAt ? (
                      nextPickupChipLabel(nextOpenAt)
                    ) : (
                      "Closed now"
                    )
                  ) : (
                    <>
                      <span className="sm:hidden">ASAP</span>
                      <span className="hidden sm:inline">ASAP · 15-25 min</span>
                    </>
                  )}
                </span>
              </div>
              {isTuscanHost ? (
                <div className="hidden h-10 items-center gap-2 rounded-[10px] border border-[#d6d6d6] bg-white px-3 text-sm font-medium text-[#0d0d0d] shadow-[0_1px_2px_rgb(0_0_0_/_0.05)] sm:inline-flex">
                  <MapPin className="h-4 w-4 text-[#707070]" />
                  <span>Southern Highlands</span>
                </div>
              ) : null}
              {/* Commission-free reassurance: hidden on small widths so the
                  pills keep a single uncrowded row on mobile. */}
              <span className="hidden items-center text-[13px] font-medium text-[#707070] md:inline-flex">
                Direct from our kitchen. No third-party fees.
              </span>
            </div>

            <div className="relative min-w-0 flex-1 lg:max-w-[360px]">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#707070] sm:left-3 sm:h-4 sm:w-4" />
              <Input
                ref={searchInputRef}
                aria-label="Search menu"
                placeholder={menuUnavailable ? "Menu temporarily unavailable" : "Search menu"}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                disabled={menuUnavailable}
                className="h-9 rounded-[10px] border-[#d6d6d6] bg-white pl-8 pr-9 text-[13px] font-medium shadow-[0_1px_2px_rgb(0_0_0_/_0.05)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 sm:h-10 sm:pl-9 sm:pr-10 sm:text-sm"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-[#707070] transition-colors hover:bg-[#f4f4f5] hover:text-[#0d0d0d] sm:right-1.5 sm:h-9 sm:w-9"
                  aria-label="Clear menu search"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </div>
          </div>

          <div className="lg:hidden">
            {availableCategories.length > 0 ? (
              <CategoryNav
                categories={availableCategories}
                activeCategory={activeCategory}
                onCategoryClick={handleCategoryClick}
              />
            ) : null}
          </div>
        </div>
      </div>

      {orderingPaused ? (
        <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
          <div
            data-ordering-paused-banner
            role="status"
            className="flex flex-col gap-3 rounded-[14px] border border-[#eadbd3] bg-[#fffaf7] px-4 py-3.5 shadow-[0_18px_48px_-38px_rgba(69,42,28,0.35)] sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-[#f6e8df] text-[#b4532a]"
              >
                <Phone className="size-4" />
              </span>
              <div className="text-[14px] font-medium leading-6 text-[#5b4a43]">
                <p>
                  Online pickup ordering is temporarily paused. To place a
                  pickup order, call {brandShortName}.
                </p>
                <p className="mt-0.5 font-bold text-[#0d0d0d]">
                  {fallbackPhone}
                </p>
              </div>
            </div>
            <a
              href={telHref(fallbackPhone)}
              className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-[#0d0d0d] px-5 text-[14px] font-bold text-white transition-colors hover:bg-[#262626]"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call to order pickup
            </a>
          </div>
        </div>
      ) : null}

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-4 sm:px-6 sm:py-5 lg:grid-cols-[256px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:py-6">
        {availableCategories.length > 0 ? (
          <CategorySidebar
            categories={availableCategories}
            activeCategory={activeCategory}
            onCategoryClick={handleCategoryClick}
          />
        ) : (
          <div className="hidden lg:block" aria-hidden="true" />
        )}
        <div className="min-w-0">
        {isLoading ? (
          <MenuGridSkeleton />
        ) : menuUnavailable ? (
          <MenuUnavailableState phone={fallbackPhone} />
        ) : menuEmpty ? (
          process.env.NODE_ENV !== "production" ? (
            <div className="mx-auto max-w-lg py-16 text-center">
              <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Menu isn&apos;t loaded yet</h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Online ordering pulls dishes from your Convex database. Sync the {hostSiteConfig.name} dinner
                menu (names, descriptions, prices, and SpotApps images from the live food menu) so this page can
                show items. This updates dinner items in place and does not wipe brunch, drinks, or other
                sections.
              </p>
              <p className="mt-4 rounded-lg bg-muted/50 px-4 py-3 text-left font-mono text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
                bun run convex:seed-tuscan
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Uses the same Convex deployment as <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">NEXT_PUBLIC_CONVEX_URL</code> in{" "}
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">.env.local</code> (the <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">--push</code> flag uploads the latest functions, then seeds).{" "}
                Run <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">npx convex dev</code> once if the CLI reports a missing access token.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Shortcut: <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">bun run convex:seed-tuscan</code>
              </p>
              <p className="mt-4 text-xs text-muted-foreground">
                Browse the{" "}
                <Link
                  href="/menu"
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  menu on this site
                </Link>
                . Seed data is aligned with the live {brandShortName} dinner menu.
              </p>
            </div>
          ) : (
            <div className="mx-auto max-w-lg py-16 text-center">
              <h2 className="text-xl font-bold tracking-tight sm:text-2xl">The menu isn&apos;t available right now</h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Give us a call at {fallbackPhone} and we&rsquo;ll help you out, or check back in a few minutes.
              </p>
            </div>
          )
        ) : searchQuery.trim() ? (
          /* Search Results View */
          <div className="min-h-[60vh] space-y-5">
            <div className="flex items-center justify-between border-b border-[#ededed] pb-3">
              <div>
                <h2 className="text-[24px] font-bold leading-8 tracking-normal text-[#0d0d0d]">
                  Search Results
                </h2>
                {searchResults.length > 0 && (
                  <p className="mt-1 text-sm font-medium text-[#707070]">
                    Found {searchResults.length} {searchResults.length === 1 ? "item" : "items"} for &ldquo;{searchQuery}&rdquo;
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                onClick={() => setSearchQuery("")}
                className="h-9 rounded-[10px] text-sm font-semibold text-[#707070] hover:text-[#0d0d0d]"
              >
                Clear
              </Button>
            </div>

            {searchResults.length > 0 ? (
              <MenuGrid
                items={searchResults}
                quickAddRequiredIds={quickAddRequiredIds}
                onSelect={setSelectedItem}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="h-20 w-20 rounded-full bg-muted/50 flex items-center justify-center mb-6">
                  <Search className="h-10 w-10 text-muted-foreground/20" />
                </div>
                <h3 className="text-xl font-bold mb-2">We couldn&rsquo;t find &ldquo;{searchQuery}&rdquo;</h3>
                <p className="text-muted-foreground max-w-xs mx-auto text-sm font-medium">
                  Try checking your spelling or searching for something else, like &quot;Pizza&quot; or &quot;Burger&quot;
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-8 sm:space-y-10">
            {servicePolicyPrograms.map(({ policy: servicePolicy, items }) => (
              <section
                key={servicePolicy.programId}
                data-menu-service-policy={servicePolicy.programId}
                className="rounded-[14px] border border-[#eadbd3] bg-[#fffaf7] px-4 py-4 sm:px-5"
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#9a6d2f]">
                  {servicePolicy.label}
                </p>
                <p className="mt-1 text-sm font-medium leading-5 text-[#5b4a43]">
                  {servicePolicy.note}
                </p>
                <p className="mt-2 text-sm font-semibold text-[#0d0d0d]">
                  {items.map((item) => item.name).join(" · ")}
                </p>
              </section>
            ))}

            {/* Guest Favorites */}
            {guestFavoriteItems.length > 0 && (
              <section
                ref={(el) => {
                  registerCategoryRef("guest-favorites")(el);
                }}
                id="category-guest-favorites"
                className="scroll-mt-[var(--order-sticky-offset,7rem)]"
              >
                <div className="mb-3 flex items-center justify-between gap-4 sm:mb-4">
                  <h2 className="text-[22px] font-bold leading-7 tracking-normal text-[#0d0d0d] sm:text-[24px] sm:leading-8">
                    Most Popular
                  </h2>
                  <span className="shrink-0 text-sm font-medium text-[#707070]">{guestFavoriteItems.length} items</span>
                </div>
                <PopularMenuGrid
                  items={guestFavoriteItems}
                  quickAddRequiredIds={quickAddRequiredIds}
                  onSelect={setSelectedItem}
                />
              </section>
            )}

            {/* Category Sections */}
            {browsingCategories.map((cat) => {
              const items = itemsByCategory[cat.id];
              if (!items || items.length === 0) return null;

              return (
                <section
                  key={cat.id}
                  ref={(el) => {
                    registerCategoryRef(cat.id)(el);
                  }}
                  id={`category-${cat.id}`}
                  className="scroll-mt-[var(--order-sticky-offset,7rem)]"
                >
                  <div className="mb-3 flex items-center justify-between gap-4 border-b border-[#ededed] pb-2.5 sm:mb-4 sm:pb-3">
                    <h2 className="flex items-center gap-2 text-[22px] font-bold leading-7 tracking-normal text-[#0d0d0d] sm:text-[24px] sm:leading-8">
                      <span className="text-[20px]" aria-hidden>{cat.icon}</span>
                      {cat.label}
                    </h2>
                    <span className="shrink-0 text-sm font-medium text-[#707070]">{items.length} items</span>
                  </div>
                  <MenuGrid
                    items={items}
                    priorityImageUrls={guestFavoritePhotoUrls}
                    quickAddRequiredIds={quickAddRequiredIds}
                    onSelect={setSelectedItem}
                  />
                </section>
              );
            })}
          </div>
        )}
        </div>
      </div>
      {/* On the /menu embed (showMenuHeader) the surface owns the single
          persistent bottom bar, because the marketing chrome wraps this page
          instead of the order layout. On the /order app the order layout owns
          the one-and-only bottom bar, so the surface renders none — keeping
          exactly one bar per surface and ending the previous double-stack. */}
      {showMenuHeader ? <MobileViewCartBanner mounted={mounted} /> : null}
      {/* One shared, lazy-loaded customization sheet for the whole surface. It
          mounts only after a card is tapped (or a deep link seeds selectedItem),
          so framer-motion + the modifiers query stay out of the initial load. */}
      {selectedItem ? (
        <ItemCustomizationSheet
          item={selectedItem}
          open={Boolean(selectedItem)}
          onOpenChange={closeSelectedItem}
        />
      ) : null}
      <DeliveryAddressSheet
        open={deliveryAddressOpen}
        onOpenChange={setDeliveryAddressOpen}
      />
    </div>
  );
}

function MenuUnavailableState({ phone }: { phone: string }) {
  const { siteConfig: hostSiteConfig } = useMarketingLocationContext();
  const brandShortName =
    hostSiteConfig.name === "Tuscan Cove Bar & Patio"
      ? "Tuscan Cove"
      : hostSiteConfig.name;
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center justify-center rounded-[14px] border border-[#eadbd3] bg-[#fffaf7] px-5 py-12 text-center shadow-[0_18px_48px_-38px_rgba(69,42,28,0.35)]">
      <h2 className="text-[22px] font-bold leading-8 tracking-normal text-[#0d0d0d]">
        Online menu is taking longer than usual
      </h2>
      <p className="mt-3 max-w-md text-[15px] font-medium leading-6 text-[#575757]">
        The kitchen can still help with pickup questions by phone, and reservations are available here on the site.
      </p>
      <div className="mt-6 flex w-full max-w-sm flex-col gap-3 sm:flex-row">
        <a
          href={telHref(phone)}
          className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-full bg-[#0d0d0d] px-5 text-[14px] font-bold text-white transition-colors hover:bg-[#262626]"
        >
          <Phone className="h-4 w-4" aria-hidden="true" />
          Call {brandShortName}
        </a>
        <Link
          href="/reservations"
          className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-full border border-[#d6d6d6] bg-white px-5 text-[14px] font-bold text-[#0d0d0d] transition-colors hover:border-[#0d0d0d]"
        >
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
          Reserve
        </Link>
      </div>
    </div>
  );
}

function CartButton({ mounted }: { mounted: boolean }) {
  const itemCount = useCartStore((state) => state.getItemCount());
  const total = useCartStore((state) => state.getTotal());

  return (
    <CartSheet>
      <Button
        type="button"
        className="relative h-10 shrink-0 rounded-full px-4 text-[11px] font-bold uppercase tracking-[0.16em]"
      >
        <ShoppingBag className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Cart</span>
        {mounted && itemCount > 0 ? (
          <>
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-[10px] font-black">
              {itemCount}
            </span>
            <span className="ml-2 hidden tabular-nums sm:inline">
              {formatPrice(total)}
            </span>
          </>
        ) : null}
      </Button>
    </CartSheet>
  );
}

function MobileViewCartBanner({ mounted }: { mounted: boolean }) {
  const itemCount = useCartStore((state) => state.getItemCount());
  const total = useCartStore((state) => state.getTotal());
  const shouldShow = mounted && itemCount > 0;

  if (!shouldShow) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 bg-gradient-to-t from-white via-white/95 to-transparent px-4 pb-[var(--sticky-bar-pb)] pt-5 lg:hidden">
      <CartSheet>
        <button
          type="button"
          aria-label={`View order, ${formatPrice(total)}`}
          className="pointer-events-auto mx-auto flex min-h-14 w-full max-w-md items-center justify-between rounded-2xl bg-[var(--roma-terracotta)] px-4 text-white shadow-[0_14px_42px_-22px_rgb(244_92_36_/_0.85)] transition-all hover:bg-[var(--roma-saffron-deep)] active:scale-[0.98]"
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/12">
              <ShoppingBag className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <span className="text-[15px] font-black">View order</span>
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-[11px] font-black tabular-nums">
                {itemCount}
              </span>
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <span className="text-lg font-black tabular-nums">
              {formatPrice(total)}
            </span>
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </span>
        </button>
      </CartSheet>
    </div>
  );
}

// Loading skeleton
function MenuGridSkeleton() {
  return (
    <div className="space-y-10">
      {[1, 2, 3].map((section) => (
        <div key={section}>
          <div className="mb-4 flex items-baseline justify-between gap-4 border-b border-[#ededed] pb-3">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-6 w-16" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="flex min-h-[172px] items-start gap-3 rounded-[12px] border border-[#ededed] p-4">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <Skeleton className="h-36 w-36 rounded-[10px] flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
